import React, { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useAppSelector } from '../../store/hooks'
import { useRoleGuard } from '../../hooks/useRoleGaurd'
import { ANALYTICS_CONFIG, initializeAnalytics, trackPageView } from '../../services/analytics'

const PAGEVIEW_DEDUPE_WINDOW_MS = 1500

let lastTrackedPageviewSignature = ''
let lastTrackedPageviewAt = 0

export const resetAnalyticsRoutePageviewDedupe = (): void => {
  lastTrackedPageviewSignature = ''
  lastTrackedPageviewAt = 0
}

export type AnalyticsPageType =
  | 'requestor-home'
  | 'requestor-create'
  | 'requestor-request-confirmation'
  | 'requestor-edit-submitted'
  | 'requestor-create-vacation-bid'
  | 'requestor-vacation-bid'
  | 'requestor-status-detail'
  | 'approver-home'
  | 'approver-process'
  | 'admin-home'
  | 'admin-manual-timeoff'
  | 'admin-manage-proxy'
  | 'admin-approver-proxy'
  | 'admin-requestor-proxy'
  | 'admin-float-proxy'

const PAGE_TYPE_TO_KEY: Record<Exclude<AnalyticsPageType, 'admin-manage-proxy'>, string> = {
  'requestor-home': ANALYTICS_CONFIG.PAGES.REQUESTOR_HOME,
  'requestor-create': ANALYTICS_CONFIG.PAGES.REQUESTOR_CREATE,
  'requestor-request-confirmation': ANALYTICS_CONFIG.PAGES.REQUESTOR_REQUEST_CONFIRMATION,
  'requestor-edit-submitted': ANALYTICS_CONFIG.PAGES.REQUESTOR_EDIT_SUBMITTED,
  'requestor-create-vacation-bid': ANALYTICS_CONFIG.PAGES.REQUESTOR_CREATE_VACATION_BID,
  'requestor-vacation-bid': ANALYTICS_CONFIG.PAGES.REQUESTOR_VACATION_BID,
  'requestor-status-detail': ANALYTICS_CONFIG.PAGES.REQUESTOR_STATUS_DETAIL,
  'approver-home': ANALYTICS_CONFIG.PAGES.APPROVER_HOME,
  'approver-process': ANALYTICS_CONFIG.PAGES.APPROVER_PROCESS,
  'admin-home': ANALYTICS_CONFIG.PAGES.ADMIN_HOME,
  'admin-manual-timeoff': ANALYTICS_CONFIG.PAGES.ADMIN_MANUAL_TIMEOFF,
  'admin-approver-proxy': ANALYTICS_CONFIG.PAGES.ADMIN_APPROVER_PROXY,
  'admin-requestor-proxy': ANALYTICS_CONFIG.PAGES.ADMIN_REQUESTOR_PROXY,
  'admin-float-proxy': ANALYTICS_CONFIG.PAGES.ADMIN_FLOAT_PROXY,
}

const resolveAnalyticsPageKey = (
  pageType: AnalyticsPageType,
  pathname: string
): string => {
  if (pageType !== 'admin-manage-proxy') {
    return PAGE_TYPE_TO_KEY[pageType]
  }

  if (pathname.includes('/admin/requestor-proxy')) {
    return ANALYTICS_CONFIG.PAGES.ADMIN_REQUESTOR_PROXY
  }

  if (pathname.includes('/admin/float-proxy')) {
    return ANALYTICS_CONFIG.PAGES.ADMIN_FLOAT_PROXY
  }

  return ANALYTICS_CONFIG.PAGES.ADMIN_APPROVER_PROXY
}

const shouldSkipDuplicatePageview = (signature: string): boolean => {
  const now = Date.now()
  const isImmediateDuplicate =
    lastTrackedPageviewSignature === signature &&
    now - lastTrackedPageviewAt < PAGEVIEW_DEDUPE_WINDOW_MS

  lastTrackedPageviewSignature = signature
  lastTrackedPageviewAt = now

  return isImmediateDuplicate
}

interface AnalyticsRouteProps {
  children: React.ReactNode
  pageType: AnalyticsPageType
  contentTitle?: string
}

/**
 * Wraps a route element to handle CJA analytics: initializes the data layer
 * once user data is available, then fires a page-view whenever the route's
 * pageType changes. Mirrors the pattern already in place for WFR and UBT.
 */
const AnalyticsRoute: React.FC<AnalyticsRouteProps> = ({ children, pageType, contentTitle }) => {
  const employee = useAppSelector((state) => state.profile?.data?.employee)
  const { isAdmin, isApprover, isApproverProxy } = useRoleGuard(['admin', 'approver', 'requestor'])
  const location = useLocation()

  const nuid = employee?.empNuid || sessionStorage.getItem('userId') || sessionStorage.getItem('nuid') || ''
  let userType: 'admin' | 'approver' | 'requestor' = 'requestor'

  if (isAdmin) {
    userType = 'admin'
  } else if (isApprover || isApproverProxy) {
    userType = 'approver'
  }

  useEffect(() => {
    const isValidAdminProxyRoute =
      pageType !== 'admin-manage-proxy' ||
      ['/admin/approver-proxy', '/admin/requestor-proxy', '/admin/float-proxy'].some((path) =>
        location.pathname.includes(path)
      )

    if (isValidAdminProxyRoute) {
      sessionStorage.setItem(
        'tort_last_valid_route',
        `${location.pathname}${location.search}${location.hash}`
      )
    }
  }, [location.pathname, location.search, location.hash, pageType])

  useEffect(() => {
    if (!nuid) return

    const analyticsPageKey = resolveAnalyticsPageKey(pageType, location.pathname)

    initializeAnalytics({
      nuid,
      region: employee?.regionCd,
      union: employee?.union,
      jobRole: employee?.job,
      userType,
    })

    const pageviewSignature = [
      analyticsPageKey,
      location.pathname,
      location.search,
      location.hash,
      nuid,
    ].join('|')

    if (!shouldSkipDuplicatePageview(pageviewSignature)) {
      trackPageView(analyticsPageKey, {
        contentTitle: contentTitle || pageType,
      })
    }
  }, [
    nuid,
    employee?.regionCd,
    employee?.union,
    employee?.job,
    userType,
    pageType,
    contentTitle,
    location.pathname,
    location.search,
    location.hash,
  ])

  return <>{children}</>
}

export default AnalyticsRoute
